import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import AppIcon from '../components/AppIcon';
import { friendlyError } from '../utils/friendlyError';
import TruckLoader from '../components/TruckLoader';
import './SolShell.css';

const KPI_LIST = [
  { code: 'TOTAL_MILES',     label: 'Total Miles' },
  { code: 'TOTAL_TRIPS',     label: 'Total Trips' },
  { code: 'TOTAL_FUEL',      label: 'Total Fuel' },
  { code: 'TOTAL_COST',      label: 'Total Cost' },
  { code: 'AVG_SPEED',       label: 'Avg Speed' },
  { code: 'ON_TIME_PCT',     label: 'On-Time %' },
  { code: 'IDLE_TIME',       label: 'Idle Time' },
  { code: 'CO2_EMISSIONS',   label: 'CO₂ Emissions' },
  { code: 'REVENUE',         label: 'Revenue' },
  { code: 'COST_PER_MILE',   label: 'Cost/Mile' },
  { code: 'UTILIZATION_RATE',label: 'Utilization' },
  { code: 'SAFETY_SCORE',    label: 'Safety Score' },
];

const defaultKpis = () => Object.fromEntries(KPI_LIST.map(k => [k.code, true]));

const UserManagement = () => {
  const navigate = useNavigate();
  const { user, signOut, isAdmin, canUpload, getIdToken, profileError, profileLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    location: '',
    role: 'user',
    canEditSourceConfig: false,
    canUpload: false,
    kpis: defaultKpis(),
  });

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

  const displayName = useMemo(() => {
    return user?.displayName || user?.email || 'User';
  }, [user]);

  const avatar = useMemo(() => {
    const basis = (user?.displayName || user?.email || 'U').trim();
    const parts = basis.split(/\s+/).filter(Boolean);
    const initials = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : `${basis[0]}`;
    return initials.toUpperCase();
  }, [user]);

  const userInsights = useMemo(() => {
    const total = users.length;
    const admins = users.filter((u) => ['admin', 'super_admin'].includes((u.role || '').toLowerCase())).length;
    const active = users.filter((u) => u.is_active !== false).length;
    const sourceEditors = users.filter((u) => !!u.can_edit_source_config).length;
    return { total, admins, active, sourceEditors };
  }, [users]);

  const USERS_CACHE_KEY = 'sol_users_cache';
  const USERS_CACHE_TTL = 90_000; // 90 seconds

  const loadUsers = async (force = false) => {
    if (!user) return;
    // Serve from sessionStorage cache for instant navigation
    if (!force) {
      try {
        const raw = sessionStorage.getItem(USERS_CACHE_KEY);
        if (raw) {
          const { ts, data } = JSON.parse(raw);
          if (Date.now() - ts < USERS_CACHE_TTL) {
            setUsers(data);
            return;
          }
        }
      } catch { /* ignore */ }
    }
    setLoading(true);
    setError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Failed to load users (${res.status})`);
      const list = Array.isArray(data?.users) ? data.users : [];
      setUsers(list);
      try { sessionStorage.setItem(USERS_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: list })); } catch { /* ignore */ }
    } catch (e) {
      setUsers([]);
      setError(friendlyError(e, 'Failed to load users.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profileLoading) return; // wait until profile is known
    if (!isAdmin) {
      navigate('/kpi-dashboard', { replace: true });
      return;
    }
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isAdmin, profileLoading]);

  const openAddModal = () => {
    setEditingUserId(null);
    setFormError('');
    setFormData({ email: '', password: '', fullName: '', phone: '', location: '', role: 'user', canEditSourceConfig: false, canUpload: false, kpis: defaultKpis() });
    setModalOpen(true);
  };

  const openEditModal = (userId) => {
    const usr = users.find((u) => u.uid === userId || u.id === userId);
    if (!usr) return;
    setEditingUserId(userId);
    setFormError('');
    const kpiAccess = usr.kpi_access?.kpis || defaultKpis();
    setFormData({
      email: usr.email || '',
      password: '',
      fullName: usr.full_name || usr.displayName || '',
      phone: usr.phone || '',
      location: usr.location || '',
      role: usr.role || 'user',
      canEditSourceConfig: !!usr.can_edit_source_config,
      canUpload: !!usr.can_upload,
      kpis: { ...defaultKpis(), ...kpiAccess },
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormError('');
  };

  const saveUser = async () => {
    setFormError('');
    const email = (formData.email || '').trim();
    const role = (formData.role || 'user').trim();

    if (!email) {
      setFormError('Email is required');
      return;
    }

    if (!editingUserId && !formData.password) {
      setFormError('Password is required for new users');
      return;
    }

    setSaving(true);
    const payload = {
      email,
      role,
      full_name: (formData.fullName || '').trim() || null,
      phone: (formData.phone || '').trim() || null,
      location: (formData.location || '').trim() || null,
      can_edit_source_config: formData.canEditSourceConfig,
      can_upload: formData.canUpload,
      kpis: formData.kpis,
    };
    if (formData.password) {
      payload.password = formData.password;
    }

    try {
      const token = await getIdToken();
      const method = editingUserId ? 'PUT' : 'POST';
      const url = editingUserId
        ? `${apiBaseUrl}/api/users/${encodeURIComponent(editingUserId)}`
        : `${apiBaseUrl}/api/users`;

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Failed to save user (${res.status})`);

      closeModal();
      loadUsers(true); // bypass cache after mutation
    } catch (e) {
      setFormError(friendlyError(e, 'Failed to save user.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    // Optimistic removal — instantly hide the row
    setDeletingId(userId);
    const prev = users;
    setUsers((u) => u.filter((x) => (x.uid || x.id) !== userId));
    try { sessionStorage.removeItem('sol_users_cache'); } catch { /* ignore */ }

    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Failed to delete user (${res.status})`);
      }
      // Deletion succeeded — row is already removed
    } catch (e) {
      setUsers(prev);  // rollback on failure
      setError(friendlyError(e, 'Failed to delete user.'));
    } finally {
      setDeletingId(null);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    if (userId === user?.uid) {
      setError("You can't change your own role.");
      return;
    }
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiBaseUrl}/api/users/${encodeURIComponent(userId)}/role`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Failed to update role (${res.status})`);
      }
      loadUsers(true); // bypass cache after role change
    } catch (e) {
      setError(friendlyError(e, 'Failed to update role.'));
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Access Denied</h2>
        <p>You don't have access, please request access.</p>
        {profileError && (
          <p style={{ color: '#b00020' }}>Profile error: {profileError}</p>
        )}
        {user?.email && (
          <p style={{ fontSize: '0.9em', color: '#666' }}>Signed in as: {user.email}</p>
        )}
        <Link to="/kpi-dashboard">Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="sol-shell">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="logo">
          <h1>
            <div className="logo-icon"><AppIcon name="truck" /></div>
            <span>SOL Transport</span>
          </h1>
        </div>

        <div className="nav-section">
          <div className="nav-section-title">Main Menu</div>
          <Link className="nav-item" to="/kpi-dashboard">
            <span className="nav-item-icon"><AppIcon name="dashboard" /></span>
            Dashboard
          </Link>

        </div>

        <div className="nav-section">
          <div className="nav-section-title">Administration</div>
          {isAdmin && <Link className="nav-item active" to="/user-management">
            <span className="nav-item-icon"><AppIcon name="users" /></span>
            User Management
          </Link>}
          {isAdmin && <Link className="nav-item" to="/source-config">
            <span className="nav-item-icon"><AppIcon name="link" /></span>
            Source Config
          </Link>}
          {isAdmin && <Link className="nav-item" to="/activity-log">
            <span className="nav-item-icon"><AppIcon name="activity" /></span>
            User Activity Log
          </Link>}
          {canUpload && <Link className="nav-item" to="/upload">
            <span className="nav-item-icon"><AppIcon name="upload" /></span>
            Upload Files
          </Link>}
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">{avatar}</div>
          <div className="user-info">
            <div className="user-name">{displayName}</div>
            <div className="user-role">Admin</div>
          </div>
          <button className="logout-btn" type="button" onClick={handleLogout} aria-label="Logout">
            <AppIcon name="login" size={14} />
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="main-content">
        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          type="button" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <AppIcon name="menu" size={18} />
        </button>

        <header className="header">
          <div className="header-left">
            <h2 className="title-with-icon"><AppIcon name="users" /> User Management</h2>
            <p>Manage users, roles, permissions, and KPI access</p>
          </div>
          <div className="header-right">
            <button className="btn btn-primary" onClick={openAddModal}>
              <AppIcon name="plus" /> Add User
            </button>
          </div>
        </header>

        {error && (
          <div style={{ padding: 12, backgroundColor: '#ffebee', color: '#b00020', borderRadius: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div className="admin-insights-grid">
          <div className="admin-insight-card">
            <div className="admin-insight-icon users"><AppIcon name="users" /></div>
            <div>
              <div className="admin-insight-value">{userInsights.total}</div>
              <div className="admin-insight-label">Total Users</div>
            </div>
          </div>
          <div className="admin-insight-card">
            <div className="admin-insight-icon shield"><AppIcon name="shield" /></div>
            <div>
              <div className="admin-insight-value">{userInsights.admins}</div>
              <div className="admin-insight-label">Admins</div>
            </div>
          </div>
          <div className="admin-insight-card">
            <div className="admin-insight-icon check"><AppIcon name="check" /></div>
            <div>
              <div className="admin-insight-value">{userInsights.active}</div>
              <div className="admin-insight-label">Active Accounts</div>
            </div>
          </div>
          <div className="admin-insight-card">
            <div className="admin-insight-icon settings"><AppIcon name="settings" /></div>
            <div>
              <div className="admin-insight-value">{userInsights.sourceEditors}</div>
              <div className="admin-insight-label">Source Editors</div>
            </div>
          </div>
        </div>

        <div className="table-card user-management-table-card">
          <div className="table-header">
            <div className="table-title">Users ({users.length})</div>
          </div>

          {loading ? (
            <div style={{ padding: '32px 0', display: 'flex', justifyContent: 'center' }}>
              <TruckLoader message="Loading users…" size="sm" />
            </div>
          ) : users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>No users found. Add one to get started.</div>
          ) : (
            <table className="data-table user-management-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Role</th>
                  <th>Source Edit</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((usr) => {
                  const userId = usr.uid || usr.id;
                  const isSelf = userId === user?.uid;
                  return (
                    <tr key={userId}>
                      <td className="um-cell-email">{usr.email}</td>
                      <td className="um-cell-name">{usr.full_name || '—'}</td>
                      <td className="um-cell-meta">{usr.phone || '—'}</td>
                      <td className="um-cell-meta">{usr.location || '—'}</td>
                      <td>
                        <select
                          value={usr.role || 'user'}
                          onChange={(e) => updateUserRole(userId, e.target.value)}
                          disabled={isSelf}
                          title={isSelf ? "You can't change your own role." : undefined}
                          className="um-role-select"
                        >
                          <option value="user">User</option>
                          <option value="uploader">Uploader</option>
                          <option value="etl_admin">ETL Admin</option>
                          <option value="admin">Admin</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </td>
                      <td>
                        <span className={`um-badge ${usr.can_edit_source_config ? 'enabled' : 'disabled'}`}>
                          <AppIcon name={usr.can_edit_source_config ? 'check' : 'ban'} size={12} />
                          {usr.can_edit_source_config ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td>
                        <span className={`um-badge ${usr.is_active !== false ? 'active' : 'inactive'}`}>
                          <AppIcon name={usr.is_active !== false ? 'check' : 'ban'} size={12} />
                          {usr.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ marginRight: 8 }}
                          onClick={() => openEditModal(userId)}
                        >
                          <AppIcon name="pencil" size={12} /> Edit
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ color: '#b00020' }}
                          onClick={() => deleteUser(userId)}
                          disabled={deletingId === userId}
                        >
                          {deletingId === userId ? <><AppIcon name="sync" size={12} /> Deleting</> : <><AppIcon name="trash" size={12} /> Delete</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* ── User Modal ── */}
      <div className={`modal-overlay${modalOpen ? ' active' : ''}`} onClick={closeModal}>
        <div className="modal um-modal" onClick={(e) => e.stopPropagation()}>

          {/* Gradient header banner */}
          <div className="um-modal-header">
            <div className="um-modal-header-icon">
              <AppIcon name={editingUserId ? 'pencil' : 'plus'} size={18} />
            </div>
            <div>
              <div className="um-modal-title">{editingUserId ? 'Edit User' : 'Add New User'}</div>
              <div className="um-modal-subtitle">{editingUserId ? 'Update account details and permissions' : 'Create a new account and assign permissions'}</div>
            </div>
            <button className="um-modal-close" onClick={closeModal} aria-label="Close">
              <AppIcon name="close" size={16} />
            </button>
          </div>

          <div className="modal-body um-modal-body">

            {/* Error banner */}
            {formError && (
              <div className="um-alert um-alert-error">
                <AppIcon name="ban" size={14} />
                {formError}
              </div>
            )}

            {/* ── Section: Identity ── */}
            <div className="um-section">
              <div className="um-section-label">
                <span className="um-section-dot" style={{ background: '#3b82f6' }} />
                Account Identity
              </div>

              <div className="form-group">
                <label className="form-label">Email Address <span>*</span></label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="user@soltransport.com"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                  disabled={!!editingUserId}
                />
              </div>

              <div className="form-row">
                {!editingUserId && (
                  <div className="form-group">
                    <label className="form-label">Password <span>*</span></label>
                    <input
                      type="password"
                      className="form-input"
                      placeholder="Min. 8 characters"
                      value={formData.password}
                      onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={(e) => setFormData((p) => ({ ...p, fullName: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="City, State"
                    value={formData.location}
                    onChange={(e) => setFormData((p) => ({ ...p, location: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* ── Section: Role ── */}
            <div className="um-section">
              <div className="um-section-label">
                <span className="um-section-dot" style={{ background: '#6366f1' }} />
                Role &amp; Permissions
              </div>

              <div className="form-group">
                <label className="form-label">Role <span>*</span></label>
                <select
                  className="form-select"
                  value={formData.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    setFormData((p) => ({ ...p, role, canUpload: role === 'uploader' ? true : p.canUpload }));
                  }}
                  disabled={!!editingUserId && editingUserId === user?.uid}
                >
                  <option value="user">User</option>
                  <option value="uploader">Uploader</option>
                  <option value="etl_admin">ETL Admin</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              {/* Toggle permissions */}
              <div className="um-toggle-group">
                {/* Source Config */}
                <div className="um-toggle-row">
                  <div className="um-toggle-info">
                    <div className="um-toggle-title">Can Edit Source Config</div>
                    <div className="um-toggle-desc">Manage data source connections and settings</div>
                  </div>
                  <button
                    type="button"
                    className={`um-toggle${formData.canEditSourceConfig ? ' on' : ''}`}
                    onClick={() => setFormData((p) => ({ ...p, canEditSourceConfig: !p.canEditSourceConfig }))}
                    aria-pressed={formData.canEditSourceConfig}
                  >
                    <span className="um-toggle-thumb" />
                  </button>
                </div>

                {/* Upload */}
                <div className="um-toggle-row">
                  <div className="um-toggle-info">
                    <div className="um-toggle-title">
                      Can Upload Files
                      {formData.role === 'uploader' && <span className="um-toggle-auto-tag">auto</span>}
                    </div>
                    <div className="um-toggle-desc">Upload WEX / Paylocity data files</div>
                  </div>
                  <button
                    type="button"
                    className={`um-toggle${(formData.role === 'uploader' || formData.canUpload) ? ' on' : ''}`}
                    onClick={() => formData.role !== 'uploader' && setFormData((p) => ({ ...p, canUpload: !p.canUpload }))}
                    aria-pressed={formData.role === 'uploader' || formData.canUpload}
                    disabled={formData.role === 'uploader'}
                  >
                    <span className="um-toggle-thumb" />
                  </button>
                </div>
              </div>
            </div>

            {/* ── Section: KPI Access ── */}
            {formData.role !== 'uploader' && (
              <div className="um-section">
                <div className="um-section-label">
                  <span className="um-section-dot" style={{ background: '#10b981' }} />
                  KPI Access
                  <div className="um-section-actions">
                    <button type="button" className="um-link-btn"
                      onClick={() => setFormData((p) => ({ ...p, kpis: defaultKpis() }))}>
                      Select All
                    </button>
                    <button type="button" className="um-link-btn"
                      onClick={() => setFormData((p) => ({ ...p, kpis: Object.fromEntries(KPI_LIST.map(k => [k.code, false])) }))}>
                      Deselect All
                    </button>
                  </div>
                </div>

                <div className="um-kpi-grid">
                  {KPI_LIST.map(({ code, label }) => (
                    <button
                      key={code}
                      type="button"
                      className={`um-kpi-pill${formData.kpis[code] ? ' on' : ''}`}
                      onClick={() => setFormData((p) => ({ ...p, kpis: { ...p.kpis, [code]: !p.kpis[code] } }))}
                    >
                      <span className="um-kpi-check">{formData.kpis[code] ? '✓' : '+'}</span>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={saveUser} disabled={saving}>
              {saving
                ? <><AppIcon name="sync" size={13} /> Saving…</>
                : editingUserId
                  ? <><AppIcon name="save" size={13} /> Update User</>
                  : <><AppIcon name="plus" size={13} /> Create User</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
